import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PageLayout from '@/components/layout/PageLayout';
import { IndicatorCard } from '@/components/ui/cards';
import type { IndicatorCardVariant } from '@/components/ui/cards';
import { Skeleton } from '@/components/ui/loading';
import { MonitoringFiltersPanel } from '@/components/features/attendance/monitoring/MonitoringFiltersPanel';
import { MonitoringRefreshBar } from '@/components/features/attendance/monitoring/MonitoringRefreshBar';
import { MonitoringAbsenteesTable } from '@/components/features/attendance/monitoring/MonitoringAbsenteesTable';
import { MonitoringJustifiedTable } from '@/components/features/attendance/monitoring/MonitoringJustifiedTable';
import { MonitoringPresentTable } from '@/components/features/attendance/monitoring/MonitoringPresentTable';
import type { LC_MonitoringFiltersIn, MonitoringCounters } from '@/api/domains/attendance/monitoring';
import { useMonitoringCounters } from '@/hooks/attendance/monitoring/useMonitoringCounters';
import { useMonitoringExport } from '@/hooks/attendance/monitoring/useMonitoringExport';
import { useMonitoringRefresh } from '@/hooks/attendance/monitoring/useMonitoringRefresh';


const COUNTER_CARDS: { key: keyof MonitoringCounters; variant: IndicatorCardVariant }[] = [
  { key: 'totalActive', variant: 'important' },
  { key: 'absent', variant: 'error' },
  { key: 'justified', variant: 'alert' },
  { key: 'present', variant: 'success' },
];

export const AttendanceMonitoringPage: React.FC = () => {
  const { t: translate } = useTranslation();

  // Último payload emitido por el panel de filtros (en vivo, sin botón "Consultar"); null hasta la
  // primera emisión.
  const [appliedFilters, setAppliedFilters] = useState<LC_MonitoringFiltersIn | null>(null);

  const handleApply = useCallback((payload: LC_MonitoringFiltersIn) => {
    setAppliedFilters(payload);
  }, []);

  const {
    containerRef,
    refreshToken,
    lastUpdateLabel,
    canManualRefresh,
    onManualRefresh,
    onRefreshCompleted,
  } = useMonitoringRefresh({ enabled: appliedFilters !== null });

  const { counters, isInitialLoading, isRefreshing } = useMonitoringCounters(
    appliedFilters,
    refreshToken,
    onRefreshCompleted,
  );

  const { isExporting, exportMonitoring } = useMonitoringExport(appliedFilters);

  const handleExport = useCallback(() => {
    void exportMonitoring('EXCEL');
  }, [exportMonitoring]);

  return (
    <PageLayout>
      <div ref={containerRef} className="flex flex-col gap-6" data-testid="attendance-monitoring-page">
        <div
          className={`grid grid-cols-2 gap-5 transition-opacity duration-200 lg:grid-cols-4 ${isRefreshing ? 'opacity-60' : ''}`}
          aria-busy={isRefreshing}
          data-testid="monitoring-counters"
        >
          {COUNTER_CARDS.map(({ key, variant }) => (
            isInitialLoading ? (
              <Skeleton
                key={key}
                height="104px"
                data-testid={`monitoring-counter-skeleton-${key}`}
              />
            ) : (
              <IndicatorCard
                key={key}
                label={translate(`attendance:monitoring.counters.${key}`)}
                value={counters ? counters[key] : 0}
                variant={variant}
                padValue={false}
                data-testid={`monitoring-counter-${key}`}
              />
            )
          ))}
        </div>

        <MonitoringFiltersPanel onApply={handleApply} />

        <MonitoringRefreshBar
          lastUpdateLabel={lastUpdateLabel}
          canManualRefresh={canManualRefresh}
          onManualRefresh={onManualRefresh}
          canExport={(counters?.totalActive ?? 0) > 0}
          isExporting={isExporting}
          onExport={handleExport}
        />

        <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-3" data-testid="monitoring-lists">
          <MonitoringAbsenteesTable appliedFilters={appliedFilters} refreshToken={refreshToken} />
          <MonitoringJustifiedTable appliedFilters={appliedFilters} refreshToken={refreshToken} />
          <MonitoringPresentTable appliedFilters={appliedFilters} refreshToken={refreshToken} />
        </div>
      </div>
    </PageLayout>
  );
};

export default AttendanceMonitoringPage;
